// src/lib/components/atoms/EndConditionAtom.tsx

import React from 'react';
import { useTranslation } from 'localization';
import InputNumberAtom from '../InputNumber/InputNumberAtom';
import DatePickerAtom from '../DatePicker/DatePickerAtom';
import PrimeReactSingleSelect, { OptionType } from '../single-select-dropdown/single-select-dropdown';
import FormRowAtom from '../FormRow/FormRowAtom';
import './EndConditionAtom.scss';

export type EndCondition = { type: 'never' } | { type: 'after'; count: number } | { type: 'onDate'; date: string };

type EndConditionType = EndCondition['type'];

export interface EndConditionAtomProps {
  /** Current end condition value */
  value: EndCondition;
  /** Called when the end condition changes */
  onChange: (value: EndCondition) => void;
  /** Optional validation errors */
  errors?: string[];
  /** Optional additional container className */
  className?: string;
  /**
   * EAINT-893: forwarded to the underlying PrimeReactSingleSelect's
   * `panelClassName` so consumers can scope the portalled dropdown
   * panel's visual (which sits outside their DOM subtree).
   */
  panelClassName?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * EndConditionAtom
 *
 * Renders a dropdown for selecting how a recurrence ends:
 *   • Never
 *   • After N occurrences
 *   • On a specific date
 *
 * Conditional inputs appear inline based on selection.
 */
const EndConditionAtom: React.FC<EndConditionAtomProps> = ({
  value,
  onChange,
  errors = [],
  className = '',
  panelClassName,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const hasError = errors.length > 0;

  // Options using OptionType format ({ name, code })
  const options: OptionType[] = [
    { code: 'never', name: t('scheduleEditor.endCondition.never') },
    { code: 'after', name: t('scheduleEditor.endCondition.after') },
    { code: 'onDate', name: t('scheduleEditor.endCondition.onDate') },
  ];

  const selectedOption = options.find((opt) => opt.code === value.type) || null;

  /**
   * Switch to a different end-condition type,
   * initializing any needed count or date defaults.
   */
  const handleTypeChange = (e: { value: OptionType | null }) => {
    const type = (e.value?.code as EndConditionType) || 'never';
    switch (type) {
      case 'never':
        onChange({ type: 'never' });
        break;
      case 'after':
        onChange({
          type: 'after',
          count: value.type === 'after' ? value.count : 10,
        });
        break;
      case 'onDate':
        onChange({
          type: 'onDate',
          date: value.type === 'onDate' ? value.date : '',
        });
        break;
    }
  };

  /**
   * When the date picker selection changes.
   */
  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      // Format as YYYY-MM-DD
      const iso = newDate.toISOString().slice(0, 10);
      onChange({ type: 'onDate', date: iso });
    }
  };

  /**
   * Convert stored date string into a Date object for the picker.
   */
  const pickerDate: Date | null = value.type === 'onDate' && value.date ? new Date(value.date) : null;

  return (
    <div className={`end-condition-atom ${className}`} data-testid={testId}>
      <FormRowAtom align="center" gap="normal">
        <span className="end-condition-atom__label">{t('scheduleEditor.endCondition.label')}</span>

        <PrimeReactSingleSelect
          value={selectedOption}
          options={options}
          onChange={handleTypeChange}
          className={`end-condition-atom__dropdown ${hasError ? 'end-condition-atom__dropdown--error' : ''}`}
          panelClassName={panelClassName}
          data-testid="end-condition-type"
        />

        {value.type === 'after' && (
          <>
            <InputNumberAtom
              value={value.count}
              onChange={(newCount) => onChange({ type: 'after', count: newCount ?? 1 })}
              type="integer"
              min={1}
              showButtons
              className={`end-condition-atom__number ${hasError ? 'end-condition-atom__number--error' : ''}`}
              data-testid="end-condition-count"
            />
            <span className="end-condition-atom__suffix">{t('scheduleEditor.endCondition.occurrences')}</span>
          </>
        )}

        {value.type === 'onDate' && (
          <DatePickerAtom
            value={pickerDate}
            onDateChange={handleDateChange}
            selectionMode="single"
            className={`end-condition-atom__date ${hasError ? 'end-condition-atom__date--error' : ''}`}
            data-testid="end-condition-date"
          />
        )}
      </FormRowAtom>

      {/* Validation errors */}
      {hasError && (
        <ul className="end-condition-atom__errors">
          {errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EndConditionAtom;
